/**
 * ProfilePage - Pagina de profil cu taburi
 * 
 * Taburi:
 * - Informații Personale (implicit)
 * - Securitate (MFA)
 * - Template-uri (doar pentru medici)
 */

import React, { useState } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Alert,
  Snackbar
} from '@mui/material';
import {
  Person as PersonIcon,
  Security as SecurityIcon,
  Article as TemplateIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { PageHeader } from '../components/ui';
import { PersonalInfoTab, SecurityTab, MyTemplatesTab } from '../components/profile';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`profile-tabpanel-${index}`}
      aria-labelledby={`profile-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 1.5 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `profile-tab-${index}`,
    'aria-controls': `profile-tabpanel-${index}`,
  };
}

const ProfilePage: React.FC = () => {
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  // Verifică dacă utilizatorul este medic sau coordinator (poate vedea tab-ul Template-uri)
  const canSeeTemplates = user?.role === 'medic' || user?.role === 'coordinator';

  if (!user) {
    return (
      <Box>
        <Alert severity="error">Nu s-a putut încărca profilul utilizatorului.</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Profil Utilizator"
      />
      
      <Box sx={{ px: 2, py: 1 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            aria-label="profile tabs"
            sx={{
              minHeight: 36,
              '& .MuiTab-root': {
                minHeight: 36,
                fontSize: '0.78rem',
                py: 0.5,
                px: 1.5,
                textTransform: 'none',
                '& .MuiSvgIcon-root': { fontSize: 16, mr: 0.5 }
              }
            }}
          >
            <Tab 
              icon={<PersonIcon />} 
              iconPosition="start" 
              label="Informații Personale" 
              {...a11yProps(0)} 
            />
            <Tab 
              icon={<SecurityIcon />} 
              iconPosition="start" 
              label="Securitate" 
              {...a11yProps(1)} 
            />
            {canSeeTemplates && (
              <Tab 
                icon={<TemplateIcon />} 
                iconPosition="start" 
                label="Template-uri" 
                {...a11yProps(2)} 
              />
            )}
          </Tabs>
        </Box>

        {/* Tab: Informații Personale */}
        <TabPanel value={tabValue} index={0}>
          <PersonalInfoTab onSnackbar={showSnackbar} />
        </TabPanel>

        {/* Tab: Securitate (MFA) */}
        <TabPanel value={tabValue} index={1}>
          <SecurityTab />
        </TabPanel>

        {/* Tab: Template-uri (doar pentru medici) */}
        {canSeeTemplates && (
          <TabPanel value={tabValue} index={2}>
            <MyTemplatesTab />
          </TabPanel>
        )}
      </Box>

      {/* Snackbar pentru notificări */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProfilePage;
